import React from "react";
import { useHistory } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { StatusPill } from "components/admin";
import { CrudSection } from "components/admin/workspace-crud";
import {
  buildSchoolTeamCreateValues,
  buildSchoolTeamFields,
  buildSchoolTeamUpdateValues,
} from "pages/app/workspace/sections/forms/school-ops-section-forms";
import { useScopedSchoolSelection } from "hooks/use-scoped-school-selection";
import { workspaceApi } from "pages/app/workspace/services";
import { hasAnyPortalAction } from "utils/portal-access";
import {
  SchoolTeamsSectionInfoRow,
  SchoolTeamsSectionSection,
  SchoolTeamsSectionThumbnailImage,
  SchoolTeamsSectionLabelText,
  SchoolTeamsSectionMetaText,
} from "pages/app/workspace/sections/styles/school-teams-section.inline.styles";

export const SchoolTeamsSection: React.FC<{
  portalAccess: any;
}> = ({ portalAccess }) => {
  const history = useHistory();
  const { schoolId } = useScopedSchoolSelection({
    portalAccess,
  });
  const teamsQuery = useQuery({
    queryKey: ["workspace-school-teams", schoolId],
    queryFn: () => workspaceApi.schoolTeams.list(schoolId),
    enabled: Boolean(schoolId),
  });
  const canCreateTeams = hasAnyPortalAction(portalAccess, [
    "school_teams.create",
    "school_teams.manage",
  ]);
  const canEditTeams = hasAnyPortalAction(portalAccess, [
    "school_teams.update",
    "school_teams.assign_coach",
    "school_teams.manage",
  ]);
  const canRemoveTeams = hasAnyPortalAction(portalAccess, [
    "school_teams.delete",
    "school_teams.manage",
  ]);
  const schoolSportsQuery = useQuery({
    queryKey: ["workspace-school-team-school-sports", schoolId],
    queryFn: () => workspaceApi.schoolSports.list(schoolId),
    enabled: Boolean(schoolId),
  });
  const ageGroupsQuery = useQuery({
    queryKey: ["workspace-school-team-age-groups"],
    queryFn: () => workspaceApi.sportAgeGroups.list(),
  });
  const roleAssignmentsQuery = useQuery({
    queryKey: ["workspace-school-team-role-assignments", schoolId],
    queryFn: () => workspaceApi.schoolRoles.list(schoolId),
    enabled: Boolean(schoolId) && (canCreateTeams || canEditTeams),
  });
  const teamFields = buildSchoolTeamFields({
    ageGroups: ageGroupsQuery.data || [],
    roleAssignments: roleAssignmentsQuery.data || [],
    schoolSports: schoolSportsQuery.data || [],
  });

  return (
    <CrudSection
      buildCreateValues={() => buildSchoolTeamCreateValues(schoolSportsQuery.data || [])}
      buildUpdateValues={buildSchoolTeamUpdateValues}
      canCreate={canCreateTeams && Boolean(schoolId)}
      canView={Boolean((teamsQuery.data || []).length)}
      canEdit={canEditTeams && Boolean(schoolId)}
      canRemove={canRemoveTeams && Boolean(schoolId)}
      columns={[
        {
          label: "Team",
          accessor: (row) => (
            <SchoolTeamsSectionInfoRow>
              <SchoolTeamsSectionSection>
                <SchoolTeamsSectionThumbnailImage
                  alt={row.name || "Team"}
                  fallbackLabel={row.name}
                  src={row.image_url || row.image}
                  variant="team"
                />
              </SchoolTeamsSectionSection>
              <div>
                <SchoolTeamsSectionLabelText>{row.name || "Unknown"}</SchoolTeamsSectionLabelText>
                <SchoolTeamsSectionMetaText>
                  {row.school_name || "School pending"}
                </SchoolTeamsSectionMetaText>
              </div>
            </SchoolTeamsSectionInfoRow>
          ),
        },
        {
          label: "Sport",
          accessor: (row) => row.sport_name || "Unknown",
        },
        {
          label: "Coach",
          accessor: (row) => row.coach?.name || row.coach_name || "Unassigned",
        },
        {
          label: "Players",
          accessor: (row) => row.player_count ?? 0,
        },
        {
          label: "Active",
          accessor: (row) => <StatusPill value={row.active ? "active" : "inactive"} />,
        },
      ]}
      createLabel="Add school team"
      data={teamsQuery.data || []}
      description={
        canCreateTeams || canEditTeams || canRemoveTeams
          ? "Browse and manage teams for the selected school. Open a team to review roster assignments, media, fixtures, and documents."
          : "Browse teams for the selected school and open a team profile for full squad context."
      }
      fields={teamFields}
      invalidateKeys={[["workspace-school-teams", schoolId]]}
      isLoading={teamsQuery.isLoading}
      onCreate={(payload) => workspaceApi.schoolTeams.create(schoolId, payload)}
      onRemove={(row) => workspaceApi.schoolTeams.remove(row.id)}
      onView={(row) => {
        if (!row?.id) {
          return;
        }
        history.push({
          pathname: `/workspace/school-teams/${row.school_id || schoolId}/${row.id}`,
          state: {
            backTo: "/workspace/school-teams",
          },
        });
      }}
      onUpdate={(row, payload) => workspaceApi.schoolTeams.update(row.id, payload)}
      removeSuccessMessage="Team removed."
      title="Teams"
    />
  );
};
